'use client'

import { useEffect, useState } from 'react'
import { cn } from '@segway/core'
import type React from 'react'

interface SafeAreaBottomProps {
  className?: string
  children?: React.ReactNode
  minHeight?: number // 最小高度（像素）
  additionalPadding?: number // 额外的内边距（像素）
}

const SafeAreaBottom = ({
  className,
  children,
  minHeight = 0,
  additionalPadding = 0,
}: SafeAreaBottomProps) => {
  const [safeAreaBottom, setSafeAreaBottom] = useState(0)
  const [isIOS, setIsIOS] = useState(false)

  useEffect(() => {
    // 检测是否为iOS设备
    const checkIsIOS = () => {
      const userAgent = window.navigator.userAgent.toLowerCase()
      return /iphone|ipad|ipod/.test(userAgent)
    }

    // 获取安全区域底部高度
    const getSafeAreaBottom = () => {
      // 获取安全区域的环境变量
      const safeAreaInset =
        getComputedStyle(document.documentElement).getPropertyValue('--sat') || '0px'
      const safeAreaValue = Number.parseInt(safeAreaInset, 10) || 0

      // 如果是iOS设备但没有检测到安全区域，使用默认值
      if (checkIsIOS() && safeAreaValue === 0) {
        return 34 // iPhone X及以上机型的默认底部安全区域高度
      }

      // 如果是Android设备但没有检测到安全区域，使用默认值
      if (!checkIsIOS() && safeAreaValue === 0) {
        return 16 // Android设备的默认底部安全区域高度
      }

      return safeAreaValue
    }

    setIsIOS(checkIsIOS())
    setSafeAreaBottom(getSafeAreaBottom())

    // 添加CSS变量到文档根元素
    const updateSafeAreaVariable = () => {
      const safeArea = getSafeAreaBottom()
      document.documentElement.style.setProperty('--safe-area-bottom', `${safeArea}px`)
    }

    updateSafeAreaVariable()
    window.addEventListener('resize', updateSafeAreaVariable)

    return () => {
      window.removeEventListener('resize', updateSafeAreaVariable)
    }
  }, [])

  // 计算最终高度（安全区域高度 + 额外内边距，但不小于最小高度）
  const finalHeight = Math.max(safeAreaBottom + additionalPadding, minHeight)

  return (
    <div
      className={cn('w-full', isIOS ? 'pb-[env(safe-area-inset-bottom,0px)]' : '', className)}
      style={{
        paddingBottom: !isIOS ? `${finalHeight}px` : undefined,
        minHeight: minHeight > 0 ? `${minHeight}px` : undefined,
      }}>
      {children}
    </div>
  )
}

export default SafeAreaBottom
